import React from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'

import { FormTemplate, SearchFormTemplateDto } from '@/core/models/form.model'
import FormTemplateSearchContainer from '@/pages/admin/forms/containers/FormTemplateSearchContainer'
import Pagination from '@/components/ui/pagination/Pagination'

type FormSelectionDialogProps = {
    open: boolean
    onClose: () => void
    onSelect: (templateId: string) => void
    templates?: FormTemplate[]
    isLoading: boolean
    searchOptions: SearchFormTemplateDto
    onSearchChange: (options: SearchFormTemplateDto) => void
    totalCount: number
    page: number
    onPageChange: (page: number, itemsPerPage: number) => void
    pageSize: number
    onPageSizeChange: (pageSize: number) => void
    error?: Error
}

export default function FormSelectionDialog({
    open,
    onClose,
    onSelect,
    templates,
    isLoading,
    searchOptions,
    onSearchChange,
    totalCount,
    page,
    onPageChange,
    pageSize,
    onPageSizeChange,
    error,
}: FormSelectionDialogProps) {
    const { t } = useTranslation()

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('page.forms.templates.search.title')}
                </DialogTitle>
                <DialogContent>
                    <FormTemplateSearchContainer
                        value={searchOptions}
                        onChange={onSearchChange}
                    />
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('common.error.loadingFailed')}
                    </Alert>
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('page.forms.templates.search.title')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <FormTemplateSearchContainer
                        value={searchOptions}
                        onChange={onSearchChange}
                    />

                    {isLoading && <LinearProgress />}

                    {!isLoading && (!templates || templates.length === 0) ? (
                        <Typography color="text.secondary" align="center" py={4}>
                            {t('page.forms.templates.search.noTemplates')}
                        </Typography>
                    ) : (
                        <>
                            <List>
                                {templates?.map((template) => (
                                    <ListItem key={template._id} disablePadding>
                                        <ListItemButton onClick={() => onSelect(template._id)}>
                                            <ListItemText
                                                primary={template.title}
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            {template.description}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} mt={1}>
                                                            {template.categories?.map((category) => (
                                                                <Chip
                                                                    key={category}
                                                                    label={category}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                            {template.tags?.map((tag) => (
                                                                <Chip
                                                                    key={tag}
                                                                    label={tag}
                                                                    size="small"
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                            <Pagination
                                totalItems={totalCount}
                                page={page}
                                onPageChange={onPageChange}
                                onItemsPerPageChange={onPageSizeChange}
                                defaultItemsPerPage={pageSize}
                                itemsPerPageOptions={[5, 10, 25]}
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    )
} 